import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  useTheme,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Chart, registerables } from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';
import FilterSelect from '@Components/Filter/Dropdown';
import { RootStateType } from '@Store';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import API from '@Services/apiAxios';
import { formatNumber, useLocale } from '../../utils/formatNumber';

Chart.register(...registerables);

const generateMonthOptions = (): DropdownOptionType[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const date = moment().subtract(i, 'months');
    return {
      value: date.format('MM-YYYY'),
      display: date.format('MMMM YYYY'),
    };
  });
};

const monthOptions = generateMonthOptions();

interface GraphDataItem {
  month: string;
  year: string;
  result: {
    sellingInvoiceCharges: number;
    buyingInvoiceCharges: number;
    grossProfit: number;
    totalWeight: number;
    totalShipment: number;
  };
}

interface ApiResponse {
  msg: string;
  data: {
    graphData: GraphDataItem[];
  };
}

const GrossProfitGraph = () => {
  const theme = useTheme();
  const locale = useLocale();
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);

  const [system, setSystem] = useState<string[]>([]);
  const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dimension, setDimension] = useState<'revenue' | 'weight' | 'shipment'>('revenue');
  const [selectedValues, setSelectedValues] = useState<{ month: string; year: string }>({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
  });

  const handleChange = (event: SelectChangeEvent<`${string}-${string}`>) => {
    const [month, year] = event.target.value.split('-');
    setSelectedValues({ month, year });
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  const fetchGrossProfitData = async () => {
    setLoading(true);
    try {
      const res = await API.get<ApiResponse>(`/analytics/stats/grossprofitmonthly-invoiced`, {
        params: {
          month: selectedValues.month,
          year: selectedValues.year,
          dimension,
          system,
        },
      });
      setGraphData(res.data.data.graphData);
    } catch (error) {
      console.error('Failed to fetch gross profit data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrossProfitData();
  }, [selectedValues, system]);

  // ✅ Mixed Chart: Selling Invoice (Bar), Buying Invoice (Bar), Dynamic Dataset (Line)
  const getDatasets = () => {
    const datasets: any[] = [
      {
        label: 'Selling Invoice Charges',
        type: 'bar',
        data: graphData.map(item => item.result.sellingInvoiceCharges),
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        yAxisID: 'y',
        order: 2, // Lower order = appears in front
      },
      {
        label: 'Buying Invoice Charges',
        type: 'bar',
        data: graphData.map(item => item.result.buyingInvoiceCharges),
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        borderWidth: 1,
        yAxisID: 'y',
        order: 3, // Behind the selling invoice
      },
    ];

    // Dynamic dataset (always line based on dimension)
    let dynamicDataset;
    const dynamicData = graphData.map(item => {
      switch (dimension) {
        case 'revenue':
          return item.result.grossProfit;
        case 'weight':
          return item.result.totalWeight;
        case 'shipment':
          return item.result.totalShipment;
        default:
          return 0;
      }
    });

    switch (dimension) {
      case 'revenue':
        dynamicDataset = {
          label: 'Gross Profit',
          type: 'line',
          data: dynamicData,
          borderColor: theme.palette.success.main, // fixed line color
          backgroundColor: 'transparent',
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: (ctx: any) => {
            const value = ctx.raw;
            return value < 0 ? 6 : 4; // negative points bigger
          },
          pointBackgroundColor: (ctx: any) => {
            const value = ctx.raw;
            return value < 0 ? theme.palette.error.main : theme.palette.success.main; // point color only
          },
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 8,
          order: 1,
        };
        break;
      case 'weight':
        dynamicDataset = {
          label: 'Total Weight',
          type: 'line',
          data: dynamicData,
          borderColor: theme.palette.info.main,
          backgroundColor: 'transparent',
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointBackgroundColor: theme.palette.info.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 8,
          order: 1, // Line appears on top
        };
        break;
      case 'shipment':
        dynamicDataset = {
          label: 'Total Shipment',
          type: 'line',
          data: dynamicData,
          borderColor: theme.palette.warning.main,
          backgroundColor: 'transparent',
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointBackgroundColor: theme.palette.warning.main,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 8,
          order: 1, // Line appears on top
        };
        break;
    }

    if (dynamicDataset) datasets.push(dynamicDataset);

    return datasets;
  };

  const chartData = {
    labels: graphData.map(item => `${item.month} ${item.year}`),
    datasets: getDatasets(),
  };

  // Get dynamic axis label based on dimension
  const getDynamicAxisLabel = () => {
    switch (dimension) {
      case 'revenue':
        return 'Gross Profit';
      case 'weight':
        return 'Total Weight (kg)';
      case 'shipment':
        return 'Total Shipment (units)';
      default:
        return 'Value';
    }
  };

  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '15px 20px', sm: '20px 40px' },
      }}
    >
      <Box
        sx={{
          color: 'rgba(24, 29, 37, 1)',
          fontSize: { xs: '20px', sm: '24px' },
          fontWeight: 600,
          lineHeight: '33.6px',
          mb: 2,
        }}
      >
        Gross Profit Margin
      </Box>

      {/* Dimension Buttons + Filters */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mt: 3,
        }}
      >
        {/* Dimension Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['revenue', 'weight', 'shipment'].map(item => {
            const isSelected = dimension === item;
            return (
              <Button
                key={item}
                onClick={() => setDimension(item as 'revenue' | 'weight' | 'shipment')}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  minWidth: 80,
                  px: 2,
                  py: 0.5,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: isSelected ? '#fff' : theme.palette.text.primary,
                  backgroundColor: isSelected ? theme.palette.primary.main : '#fff',
                  border: isSelected ? 'none' : `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                  borderRadius: '10px',
                  margin: '0 4px',
                  padding: '8px 24px',
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                  },
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </Button>
            );
          })}
        </Box>

        {/* Filters */}
        <Box
          sx={{
            width: { xs: '100%', sm: '30%' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Select month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={`${selectedValues.month}-${selectedValues.year}`}
                onChange={handleChange}
                label="Select month"
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <FilterSelect
              id="system"
              label="Select system"
              options={loggedInUserSystemDropdownList}
              value={system}
              onChange={onSystemFilter}
            />
          </Box>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ReactChart
            type="bar"
            data={chartData}
            options={{
              responsive: true,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'top',
                  align: 'end',
                  labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 15,
                  },
                },
                tooltip: {
                  callbacks: {
                    label: context => {
                      const value = context.raw as number;
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += formatNumber(value, locale);
                      }
                      return label;
                    },
                  },
                },
              },
              scales: {
                y: {
                  type: 'linear',
                  position: 'left',
                  title: {
                    display: true,
                    text: 'Invoice Charges',
                  },
                  beginAtZero: true,
                },
                y1: {
                  type: 'linear',
                  position: 'right',
                  title: {
                    display: true,
                    text: getDynamicAxisLabel(),
                  },
                  grid: {
                    drawOnChartArea: false,
                  },
                  beginAtZero: dimension !== 'revenue', // Don't force zero for revenue (may have negative)
                },
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default GrossProfitGraph;
